import type { ApolloLink, ErrorLike } from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { useLazyQuery, useMutation } from '@apollo/client/react';

import {
  AddAttributionDetailsInput,
  AddAttributionDetailsMutation,
  AttributionDetails,
} from '@generated/gql/graphql';
import { AssistantType } from '@src/types/attribution';
import {
  addAttributionDetailsMutation,
  addBasketAttributionFromAppointmentMutation,
  getBasketAttribution,
} from '@utils/queries/attribution';

import { useSnackbarError } from './useSnackbarErrors';
import { useUserInformation } from './useUserInformation';

const getErrorMessage = (error: ErrorLike): ErrorLike => {
  const baseMessage = CombinedGraphQLErrors.is(error)
    ? error.errors[0]?.message
    : error.message;
  return new Error(`Basket attribution failed: ${baseMessage}`);
};

const extractAttributionDetails = (
  result: ApolloLink.Result<AddAttributionDetailsMutation> | unknown[] | void
): Nullable<AttributionDetails> => {
  if (!result) {
    return null;
  }

  if (Array.isArray(result)) {
    const lastResult = result[result.length - 1] as
      | ApolloLink.Result<AddAttributionDetailsMutation>
      | undefined;
    return lastResult?.data?.addAttributionDetails ?? null;
  }

  return result?.data?.addAttributionDetails ?? null;
};

export const useAttribution = () => {
  const userInformation = useUserInformation();
  const [addAttribution, { loading }] = useMutation(
    addAttributionDetailsMutation
  );
  const [addBasketAttributionFromAppointment, { loading: loadingAppointment }] =
    useMutation(addBasketAttributionFromAppointmentMutation);
  const [fetchAttribution, { loading: fetchingAttribution }] =
    useLazyQuery(getBasketAttribution);

  const setSnackbarError = useSnackbarError();

  const addAttributionDetails = (
    basketId: string,
    details: AddAttributionDetailsInput
  ) =>
    addAttribution({
      variables: {
        basketId,
        details,
      },
    }).catch((error) => {
      setSnackbarError(getErrorMessage(error));
      throw error;
    });

  const addAttributionFromAppointment = (
    appointmentId: string,
    basketId: string
  ) =>
    addBasketAttributionFromAppointment({
      variables: {
        id: appointmentId,
        basketId,
      },
    })
      .then(() => null)
      .catch((error) => {
        setSnackbarError(getErrorMessage(error));
        throw error;
      });

  const fetchBasketAttribution = (basketId: string) =>
    fetchAttribution({
      variables: {
        basketId,
      },
    }).catch((error) => {
      setSnackbarError(getErrorMessage(error));
      throw error;
    });

  const addAssistantDetails = async (
    basketId: string,
    includeReferrer = true
  ) => {
    const promises: Promise<unknown>[] = [];

    if (
      userInformation?.type === AssistantType.SALES &&
      userInformation.email
    ) {
      promises.push(
        addAttributionDetails(basketId, {
          assistant: {
            sales: {
              oktaUsername: userInformation.email,
            },
          },
        })
      );

      if (includeReferrer) {
        promises.push(
          addAttributionDetails(basketId, {
            referrer: {
              sales: {
                oktaUsername: userInformation.email,
              },
            },
          })
        );
      }
    }

    if (
      userInformation?.type === AssistantType.PARTNER &&
      userInformation.partnerPositionId
    ) {
      promises.push(
        addAttributionDetails(basketId, {
          assistant: {
            partner: {
              positionId: userInformation.partnerPositionId,
              id: userInformation.partnerId,
            },
          },
        })
      );

      if (includeReferrer) {
        promises.push(
          addAttributionDetails(basketId, {
            referrer: {
              partner: {
                positionId: userInformation.partnerPositionId,
              },
            },
          })
        );
      }
    }

    return Promise.all(promises).then((result) =>
      extractAttributionDetails(result)
    );
  };

  return {
    addAttributionDetails,
    addAssistantDetails,
    fetchBasketAttribution,
    addAttributionFromAppointment,
    loading: loading || loadingAppointment || fetchingAttribution,
  };
};
